import { API_CONFIG } from '@/lib/api/config';

/**
 * Turn a path under the API base into a fully-qualified URL.
 *
 * `VITE_API_BASE_URL` is NOT the same shape in every environment, and that is
 * the whole reason this exists:
 *   - locally it is unset, so `API_CONFIG.BASE_URL` falls back to the absolute
 *     `http://localhost:3001/api/v1` — a developer sees a complete URL;
 *   - on demo / prod it is set to the RELATIVE `/api/v1`, because the SPA and
 *     the backend share one origin and CloudFront proxies `/api/*` across.
 *
 * So anything that merely concatenates onto the base looks correct in dev and
 * silently degrades to a hostless `/api/v1/…` in the deployed environments.
 * That is invisible for a fetch (the browser resolves it against the current
 * origin) but broken for any string a HUMAN copies out of the UI and pastes
 * somewhere else — a webhook URL in a payment gateway's dashboard, a link in an
 * email. Those need a host.
 *
 * `/api/*` is proxied to the backend in EVERY environment (Vite dev proxy,
 * demo/prod CloudFront), so anchoring a relative base to `window.location.origin`
 * yields a genuinely reachable public URL.
 *
 * @param path Path relative to the API base, e.g. `/webhooks/razorpay`.
 */
export function absoluteApiUrl(path: string): string {
  const base = (API_CONFIG.BASE_URL || '').replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;

  // Already absolute (local default, or a deployment using a separate api.*
  // domain) → nothing to anchor.
  if (/^https?:\/\//i.test(base)) return `${base}${suffix}`;

  // Relative or empty → anchor to the current origin. Guarded for the non-DOM
  // case (tests, SSR) where the best available answer is the relative path
  // rather than a URL with `undefined` in it.
  const relativeBase = base || '/api/v1';
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${relativeBase}${suffix}`;
  }
  return `${relativeBase}${suffix}`;
}
